import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  IconButton,
  InputAdornment,
  Chip,
  CircularProgress,
  Divider,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import GitHubIcon from '@mui/icons-material/GitHub';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Card from './Card';
import api from '../services/api';

function SettingsPage() {
  const [patValue, setPatValue] = useState('');
  const [showPat, setShowPat] = useState(false);
  const [configured, setConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const fetchStatus = useCallback(async () => {
    try {
      const data = await api.getGithubCredentialStatus();
      setConfigured(data.configured);
    } catch {
      setFeedback({ severity: 'error', message: 'Failed to load credential status.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleSave = async () => {
    const trimmed = patValue.trim();
    if (!trimmed) return;

    setSaving(true);
    setFeedback(null);
    try {
      await api.saveGithubCredentials(trimmed);
      setConfigured(true);
      setPatValue('');
      setFeedback({ severity: 'success', message: 'GitHub PAT saved successfully.' });
    } catch (err) {
      setFeedback({ severity: 'error', message: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      await api.deleteGithubCredentials();
      setConfigured(false);
      setFeedback({ severity: 'info', message: 'GitHub PAT removed.' });
    } catch (err) {
      setFeedback({ severity: 'error', message: err.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 720 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
        Settings
      </Typography>

      <Card title="GitHub Integration" headerAction={
        configured
          ? <Chip icon={<CheckCircleIcon />} label="Configured" color="success" size="small" variant="outlined" />
          : <Chip icon={<ErrorOutlineIcon />} label="Not Configured" color="warning" size="small" variant="outlined" />
      }>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            A GitHub Personal Access Token (PAT) is required to create pull requests under your account
            when approving healing jobs. The token needs <strong>write access</strong> to the target repository.
          </Typography>

          <Alert severity="info" variant="outlined" sx={{ fontSize: '0.8rem' }}>
            <strong>Required token permissions:</strong> Contents (read &amp; write), Pull requests (read &amp; write)
            on the <code>efundamentals/config</code> repository.
            Create a fine-grained PAT at{' '}
            <a href="https://github.com/settings/tokens?type=beta" target="_blank" rel="noopener noreferrer">
              github.com/settings/tokens
            </a>.
          </Alert>

          <Divider sx={{ my: 1 }} />

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <GitHubIcon sx={{ mt: 1.5, color: 'text.secondary' }} />
            <TextField
              fullWidth
              size="small"
              label="Personal Access Token"
              placeholder={configured ? '••••••••••••••••••••' : 'ghp_xxxxxxxxxxxxxxxxxxxx'}
              value={patValue}
              onChange={(e) => setPatValue(e.target.value)}
              type={showPat ? 'text' : 'password'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowPat(!showPat)} edge="end">
                      {showPat ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              variant="contained"
              size="small"
              onClick={handleSave}
              disabled={saving || !patValue.trim()}
            >
              {saving ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
              {configured ? 'Update Token' : 'Save Token'}
            </Button>
            {configured && (
              <Button
                variant="outlined"
                size="small"
                color="error"
                startIcon={<DeleteOutlineIcon />}
                onClick={handleRemove}
                disabled={saving}
              >
                Remove
              </Button>
            )}
          </Box>

          {feedback && (
            <Alert severity={feedback.severity} sx={{ mt: 1 }} onClose={() => setFeedback(null)}>
              {feedback.message}
            </Alert>
          )}
        </Box>
      </Card>
    </Box>
  );
}

export default SettingsPage;
